import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  AlertTriangle,
  Wallet as WalletIcon,
  Inbox,
  Coins,
  PlusCircle,
  Banknote,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageGuard } from '@components/PageGuard';
import { StatCard } from '@components/dashboard/StatCard';
import { TreasuryOperationModal } from '@components/finance/TreasuryOperationModal';
import { OwnerPicker, OwnerTypeToggle } from '@components/finance/OwnerPicker';
import type { OwnerSearchResult, OwnerSearchType } from '@services/search.service';
import {
  HELD_INCOMING_NOTE,
  RAW_AVAILABLE_NOTE,
  type LedgerWallet,
} from '@services/ledger.service';
import {
  isStuckHold,
  treasuryService,
  type MissingWalletType,
  type OwnerOutstandingHold,
  type ProvisionableOwnerType,
  type TreasuryOperation,
} from '@services/treasury.service';
import { cn, formatCurrency } from '@lib/utils';
import {
  chipTxnType,
  formatTimestamp,
  humanizeCode,
  labelTxnType,
  ownerTypeLabel,
  shortId,
} from './ledgerFormat';

const thClass =
  'text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] whitespace-nowrap';

type Section = 'transactions' | 'holds' | 'withdrawals';

/** `system` owners exist in the ledger but cannot be searched by name. */
function isSearchableOwnerType(value: string): value is OwnerSearchType {
  return value === 'user' || value === 'business' || value === 'branch';
}

/** The wallet the treasury modal is currently pointed at. */
interface ModalTarget {
  walletId: string;
  walletLabel?: string | null;
  walletTypeCode?: string | null;
  operation?: TreasuryOperation;
  referenceType?: string | null;
  referenceId?: string | null;
  amount?: number;
}

export const OwnerLedgerPage = () => {
  const { ownerType = '', ownerId = '' } = useParams<{
    ownerType: string;
    ownerId: string;
  }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [section, setSection] = useState<Section>('transactions');
  const [modalTarget, setModalTarget] = useState<ModalTarget | null>(null);

  // Switching owner without hand-editing the URL.
  const [switchType, setSwitchType] = useState<OwnerSearchType>(
    isSearchableOwnerType(ownerType) ? ownerType : 'business',
  );
  const [switchOwner, setSwitchOwner] = useState<OwnerSearchResult | null>(null);

  // Keep the toggle honest when the route changes under it (back/forward).
  useEffect(() => {
    if (isSearchableOwnerType(ownerType)) setSwitchType(ownerType);
  }, [ownerType]);

  const goToOwner = (owner: OwnerSearchResult | null) => {
    setSwitchOwner(null);
    if (owner) navigate(`/finance/owners/${owner.ownerType}/${owner.ownerId}`);
  };

  const ledgerKey = ['treasury', 'owner-ledger', ownerType, ownerId];

  const { data, isLoading, error } = useQuery({
    queryKey: ledgerKey,
    queryFn: () => treasuryService.getOwnerLedger(ownerType, ownerId),
    enabled: !!ownerType && !!ownerId,
  });

  // Provisioning is the fix for "the payout failed because there was no wallet".
  const provision = useMutation({
    mutationFn: (walletTypeCode: string) =>
      treasuryService.provisionWallet({
        ownerType: ownerType as ProvisionableOwnerType,
        ownerId,
        walletTypeCode,
      }),
    onSuccess: (result, walletTypeCode) => {
      toast.success(
        result.created
          ? `${humanizeCode(walletTypeCode)} wallet created.`
          : `${humanizeCode(walletTypeCode)} wallet already existed.`,
      );
      queryClient.invalidateQueries({ queryKey: ledgerKey });
      queryClient.invalidateQueries({ queryKey: ['ledger'] });
    },
    onError: (err: unknown) => {
      const message =
        (err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : null) ?? 'Could not provision that wallet.';
      toast.error(message);
    },
  });

  const wallets = data?.wallets ?? [];
  const missing = data?.missingWalletTypes ?? [];
  const holds = data?.outstandingHolds ?? [];
  const transactions = data?.recentTransactions ?? [];
  const withdrawals = data?.withdrawalRequests ?? [];

  const stuckHoldCount = useMemo(
    () => holds.filter((h) => isStuckHold(h.basketStatus, h.holdClass)).length,
    [holds],
  );

  const walletLabelById = useMemo(() => {
    const map = new Map<string, string>();
    wallets.forEach((w) => map.set(w.walletId, w.typeName || humanizeCode(w.typeCode)));
    return map;
  }, [wallets]);

  const openModal = (target: ModalTarget) => setModalTarget(target);

  const sections: { id: Section; label: string; count: number }[] = [
    { id: 'transactions', label: 'Transactions', count: transactions.length },
    { id: 'holds', label: 'Outstanding holds', count: holds.length },
    { id: 'withdrawals', label: 'Withdrawal requests', count: withdrawals.length },
  ];

  const holdWalletLabel = (h: OwnerOutstandingHold) =>
    walletLabelById.get(h.walletId) ??
    (h.walletTypeCode ? humanizeCode(h.walletTypeCode) : shortId(h.walletId));

  return (
    <PageGuard permission="wallets.read">
      <div className="space-y-8">
        <Link
          to="/finance/wallets"
          className="inline-flex items-center gap-2 text-[13px] font-medium text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          All wallets
        </Link>

        {/* Switch owner — no URL editing, no UUIDs */}
        <section className="space-y-4 rounded-3xl bg-[var(--bg-surface)] p-6 shadow-soft">
          <div className="space-y-1">
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
              Open another owner
            </h2>
            <p className="text-[13px] text-[var(--text-muted)]">
              Search by name. The phone and email on each result are there so you can
              confirm it is the right record before you look at their money.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[auto_minmax(0,1fr)] lg:items-end">
            <OwnerTypeToggle label="Owner type" value={switchType} onChange={setSwitchType} />
            <OwnerPicker
              label="Who"
              ownerType={switchType}
              value={switchOwner}
              onChange={goToOwner}
            />
          </div>
        </section>

        {isLoading && (
          <div className="rounded-3xl bg-[var(--bg-surface)] p-12 text-center text-[14px] text-[var(--text-muted)] shadow-soft">
            Loading this owner's money...
          </div>
        )}

        {!!error && !isLoading && (
          <div className="rounded-3xl bg-[var(--bg-surface)] p-12 text-center text-[14px] text-[var(--state-danger)] shadow-soft">
            Failed to load the ledger for this owner.
          </div>
        )}

        {data && (
          <>
            {/* Identity */}
            <div className="space-y-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
                {ownerTypeLabel[data.owner.ownerType as keyof typeof ownerTypeLabel] ??
                  data.owner.ownerType}
              </p>
              <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
                {data.owner.label ?? shortId(data.owner.ownerId)}
              </h1>
              <p className="break-all text-[13px] text-[var(--text-muted)]">
                {data.owner.ownerId}
              </p>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              <StatCard
                label="Balance"
                value={formatCurrency(data.totals.sumBalance)}
                icon={WalletIcon}
                variant="green"
                footer={`Across ${wallets.length} wallet${wallets.length === 1 ? '' : 's'}`}
              />
              <StatCard
                label="Incoming (earmarked)"
                value={formatCurrency(data.totals.sumHeldIncoming)}
                icon={Inbox}
                variant="purple"
                footer={HELD_INCOMING_NOTE}
              />
              <StatCard
                label="Stranded"
                value={formatCurrency(data.totals.sumHeldStranded)}
                icon={AlertTriangle}
                variant={data.totals.sumHeldStranded > 0 ? 'red' : 'green'}
                footer={
                  stuckHoldCount > 0
                    ? `${stuckHoldCount} hold${stuckHoldCount === 1 ? '' : 's'} sitting on a finished basket — clear below`
                    : 'Nothing held against a finished basket'
                }
              />
              <StatCard
                label="Spendable"
                value={formatCurrency(data.totals.sumSpendable)}
                icon={Coins}
                variant="purple"
                footer="Balance minus holds that genuinely lock it"
              />
            </div>

            {/* DB figures stay visible — demoted, never hidden. */}
            <p className="text-[12px] text-[var(--text-muted)]">
              {RAW_AVAILABLE_NOTE}:{' '}
              <span className="font-medium text-[var(--text-secondary)]">
                {formatCurrency(data.totals.sumAvailable)}
              </span>{' '}
              · held {formatCurrency(data.totals.sumHeld)}. Negative raw available
              means escrow has earmarked money FOR these wallets that has not landed
              yet — normal while orders are in flight.
            </p>

            {/* Missing wallets — the "can't send money, no wallet exists" fix */}
            {missing.length > 0 && (
              <section className="space-y-4 rounded-3xl border border-[var(--state-warning-border)] bg-[var(--state-warning-bg)] p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle
                    className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-warning)]"
                    strokeWidth={1.5}
                  />
                  <div>
                    <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">
                      Missing wallets
                    </h2>
                    <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      This owner has no wallet of these types. Money addressed to a
                      wallet that does not exist cannot be paid out — provision it
                      here and the payout can be retried.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 border-t border-[var(--state-warning-border)] pt-4">
                  {missing.map((m: MissingWalletType) => (
                    <button
                      key={m.code}
                      type="button"
                      onClick={() => provision.mutate(m.code)}
                      disabled={provision.isPending}
                      className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-2.5 text-[13px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <PlusCircle className="h-4 w-4" strokeWidth={1.5} />
                      Provision {m.name || humanizeCode(m.code)}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Wallets */}
            <section className="space-y-4">
              <div className="space-y-1">
                <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                  Wallets
                </h2>
                <p className="text-[13px] text-[var(--text-muted)]">
                  Every wallet this owner holds. Open one for its full statement, or
                  manage money on it directly.
                </p>
              </div>

              {wallets.length === 0 && (
                <div className="rounded-3xl bg-[var(--bg-surface)] p-12 text-center text-[14px] text-[var(--text-muted)] shadow-soft">
                  This owner has no wallets yet.
                </div>
              )}

              {wallets.length > 0 && (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {wallets.map((w: LedgerWallet) => (
                    <div
                      key={w.walletId}
                      className={cn(
                        'rounded-3xl bg-[var(--bg-surface)] p-6 shadow-soft',
                        w.heldStranded > 0 && 'border border-[var(--state-danger-border)]',
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-[15px] font-semibold text-[var(--text-primary)]">
                            {w.typeName || humanizeCode(w.typeCode)}
                          </div>
                          <div className="truncate text-[12px] text-[var(--text-muted)]">
                            {w.typeCode}
                          </div>
                        </div>
                        {w.heldStranded > 0 && (
                          <AlertTriangle
                            className="h-4 w-4 shrink-0 text-[var(--state-danger)]"
                            strokeWidth={1.5}
                          />
                        )}
                      </div>

                      <div className="mt-4 font-display text-[28px] font-medium leading-none tracking-[-0.03em] text-[var(--text-primary)]">
                        {formatCurrency(w.balance, w.currency)}
                      </div>

                      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-[var(--border-subtle)] pt-4">
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                            Incoming
                          </div>
                          <div
                            className="text-[14px] font-medium text-[var(--text-secondary)]"
                            title={HELD_INCOMING_NOTE}
                          >
                            {formatCurrency(w.heldIncoming, w.currency)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                            Stranded
                          </div>
                          <div
                            className={cn(
                              'text-[14px]',
                              w.heldStranded > 0
                                ? 'font-semibold text-[var(--state-danger)]'
                                : 'font-medium text-[var(--text-muted)]',
                            )}
                          >
                            {formatCurrency(w.heldStranded, w.currency)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                            Spendable
                          </div>
                          <div className="text-[14px] font-medium text-[var(--text-primary)]">
                            {formatCurrency(w.spendable, w.currency)}
                          </div>
                        </div>
                      </div>

                      {/* DB value stays auditable, in small print. */}
                      <p className="mt-3 text-[11px] text-[var(--text-muted)]">
                        {RAW_AVAILABLE_NOTE}:{' '}
                        {formatCurrency(w.availableBalance, w.currency)} · held{' '}
                        {formatCurrency(w.heldBalance, w.currency)}
                      </p>

                      <div className="mt-5 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            openModal({
                              walletId: w.walletId,
                              walletLabel: w.ownerLabel ?? data.owner.label,
                              walletTypeCode: w.typeCode,
                            })
                          }
                          className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)]"
                        >
                          <Banknote className="h-4 w-4" strokeWidth={1.5} />
                          Manage money
                        </button>
                        <Link
                          to={`/finance/wallets/${w.walletId}`}
                          className="rounded-full px-3 py-2 text-[13px] font-medium text-[var(--text-accent)] hover:underline"
                        >
                          Statement
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Sections */}
            <section className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSection(s.id)}
                    className={cn(
                      'rounded-full border px-4 py-2 text-[13px] font-medium transition-colors',
                      section === s.id
                        ? 'border-[var(--border-strong)] bg-[var(--bg-elevated)] text-[var(--text-primary)]'
                        : 'border-[var(--border-default)] text-[var(--text-muted)] hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]',
                    )}
                  >
                    {s.label}
                    <span className="ml-2 text-[var(--text-muted)]">{s.count}</span>
                  </button>
                ))}
              </div>

              <div className="overflow-hidden rounded-3xl bg-[var(--bg-surface)] shadow-soft">
                {/* Transactions */}
                {section === 'transactions' && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[var(--bg-base)]">
                        <tr>
                          <th className={thClass}>When</th>
                          <th className={thClass}>Wallet</th>
                          <th className={thClass}>Type</th>
                          <th className={thClass}>Amount</th>
                          <th className={thClass}>Balance after</th>
                          <th className={thClass}>Moved to / from</th>
                          <th className={thClass}>Reference</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--border-subtle)]">
                        {transactions.map((t) => (
                          <tr key={t.id} className="transition-colors hover:bg-[var(--bg-base)]">
                            <td className="whitespace-nowrap px-6 py-4 text-[13px] text-[var(--text-muted)]">
                              {formatTimestamp(t.createdAt)}
                            </td>
                            <td className="px-6 py-4 text-[13px]">
                              {t.walletId ? (
                                <Link
                                  to={`/finance/wallets/${t.walletId}`}
                                  className="text-[var(--text-accent)] hover:underline"
                                >
                                  {walletLabelById.get(t.walletId) ??
                                    (t.walletTypeCode
                                      ? humanizeCode(t.walletTypeCode)
                                      : shortId(t.walletId))}
                                </Link>
                              ) : (
                                <span className="text-[var(--text-muted)]">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={cn(
                                  'whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold',
                                  chipTxnType(t.transactionType),
                                )}
                              >
                                {labelTxnType(t.transactionType)}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-[14px] font-medium text-[var(--text-primary)]">
                              {formatCurrency(t.amount)}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-[14px] text-[var(--text-primary)]">
                              {formatCurrency(t.runningBalance)}
                            </td>
                            <td className="px-6 py-4 text-[13px]">
                              {t.counterpartyWalletId ? (
                                <Link
                                  to={`/finance/wallets/${t.counterpartyWalletId}`}
                                  className="text-[var(--text-accent)] hover:underline"
                                >
                                  {t.counterpartyLabel ?? shortId(t.counterpartyWalletId)}
                                </Link>
                              ) : (
                                <span className="text-[var(--text-muted)]">—</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-[13px]">
                              {t.referenceId ? (
                                <Link
                                  to={`/finance/trace/${t.referenceId}`}
                                  className="text-[var(--text-accent)] hover:underline"
                                >
                                  {t.referenceType ?? 'reference'} {shortId(t.referenceId)}
                                </Link>
                              ) : (
                                <span className="text-[var(--text-muted)]">—</span>
                              )}
                            </td>
                          </tr>
                        ))}

                        {transactions.length === 0 && (
                          <tr>
                            <td
                              colSpan={7}
                              className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]"
                            >
                              No transactions on this owner's wallets yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Outstanding holds */}
                {section === 'holds' && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[var(--bg-base)]">
                        <tr>
                          <th className={thClass}>Wallet</th>
                          <th className={thClass}>Reference</th>
                          <th className={thClass}>Basket status</th>
                          <th className={thClass}>Amount held</th>
                          <th className={thClass}>Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--border-subtle)]">
                        {holds.map((h, i) => {
                          const stuck = isStuckHold(h.basketStatus, h.holdClass);
                          return (
                            <tr
                              key={`${h.walletId}-${h.referenceId ?? i}`}
                              className="transition-colors hover:bg-[var(--bg-base)]"
                            >
                              <td className="px-6 py-4 text-[13px]">
                                <Link
                                  to={`/finance/wallets/${h.walletId}`}
                                  className="text-[var(--text-accent)] hover:underline"
                                >
                                  {holdWalletLabel(h)}
                                </Link>
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-[13px]">
                                {h.referenceId ? (
                                  <Link
                                    to={`/finance/trace/${h.referenceId}`}
                                    className="text-[var(--text-accent)] hover:underline"
                                  >
                                    {h.referenceType ?? 'reference'} {shortId(h.referenceId)}
                                  </Link>
                                ) : (
                                  <span className="text-[var(--text-muted)]">—</span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                {h.basketStatus ? (
                                  <span
                                    className={cn(
                                      'whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                                      stuck
                                        ? 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]'
                                        : 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
                                    )}
                                  >
                                    {h.basketStatus.replace(/_/g, ' ')}
                                  </span>
                                ) : (
                                  <span className="text-[13px] text-[var(--text-muted)]">—</span>
                                )}
                              </td>
                              <td
                                className={cn(
                                  'whitespace-nowrap px-6 py-4 text-[14px] font-medium',
                                  stuck
                                    ? 'text-[var(--state-danger)]'
                                    : 'text-[var(--text-secondary)]',
                                )}
                              >
                                {formatCurrency(h.amount)}
                                <div className="text-[11px] font-normal text-[var(--text-muted)]">
                                  {stuck ? 'stranded' : 'incoming — in escrow'}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                {/* A hold on a finished basket is money nobody can
                                    ever spend — that is the one worth unsticking. */}
                                {stuck ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      openModal({
                                        walletId: h.walletId,
                                        walletLabel: data.owner.label,
                                        walletTypeCode: h.walletTypeCode,
                                        operation: 'adjust-hold',
                                        referenceType: h.referenceType,
                                        referenceId: h.referenceId,
                                        // Positive amount; the modal's direction
                                        // toggle (defaulting to "Release hold")
                                        // carries the intent. The backend rejects
                                        // non-positive amounts.
                                        amount: Math.abs(h.amount),
                                      })
                                    }
                                    className="whitespace-nowrap rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-4 py-2 text-[13px] font-medium text-[var(--state-danger)] transition-colors hover:border-[var(--state-danger)]"
                                  >
                                    Adjust hold
                                  </button>
                                ) : (
                                  <span className="text-[13px] text-[var(--text-muted)]">
                                    Order still open
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}

                        {holds.length === 0 && (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]"
                            >
                              Nothing is held against this owner.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Withdrawal requests */}
                {section === 'withdrawals' && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[var(--bg-base)]">
                        <tr>
                          <th className={thClass}>Requested</th>
                          <th className={thClass}>Wallet</th>
                          <th className={thClass}>Amount</th>
                          <th className={thClass}>Status</th>
                          <th className={thClass}>Completed</th>
                          <th className={thClass}>Reference</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--border-subtle)]">
                        {withdrawals.map((wr) => (
                          <tr key={wr.id} className="transition-colors hover:bg-[var(--bg-base)]">
                            <td className="whitespace-nowrap px-6 py-4 text-[13px] text-[var(--text-muted)]">
                              {formatTimestamp(wr.createdAt)}
                            </td>
                            <td className="px-6 py-4 text-[13px]">
                              {wr.walletId ? (
                                <Link
                                  to={`/finance/wallets/${wr.walletId}`}
                                  className="text-[var(--text-accent)] hover:underline"
                                >
                                  {walletLabelById.get(wr.walletId) ??
                                    (wr.walletTypeCode
                                      ? humanizeCode(wr.walletTypeCode)
                                      : shortId(wr.walletId))}
                                </Link>
                              ) : (
                                <span className="text-[var(--text-muted)]">—</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-[14px] font-medium text-[var(--text-primary)]">
                              {formatCurrency(wr.amount)}
                            </td>
                            <td className="px-6 py-4">
                              <span className="whitespace-nowrap rounded-full bg-[var(--bg-base)] px-2.5 py-1 text-[11px] font-semibold capitalize text-[var(--text-muted)]">
                                {wr.status?.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-[13px] text-[var(--text-muted)]">
                              {formatTimestamp(wr.completedAt)}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-[13px]">
                              {wr.referenceId ? (
                                <Link
                                  to={`/finance/trace/${wr.referenceId}`}
                                  className="text-[var(--text-accent)] hover:underline"
                                >
                                  {wr.referenceType ?? 'reference'} {shortId(wr.referenceId)}
                                </Link>
                              ) : (
                                <span className="text-[var(--text-muted)]">—</span>
                              )}
                            </td>
                          </tr>
                        ))}

                        {withdrawals.length === 0 && (
                          <tr>
                            <td
                              colSpan={6}
                              className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]"
                            >
                              No withdrawal requests.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      {modalTarget && (
        <TreasuryOperationModal
          open
          onClose={() => setModalTarget(null)}
          walletId={modalTarget.walletId}
          walletLabel={modalTarget.walletLabel}
          walletTypeCode={modalTarget.walletTypeCode}
          defaultOperation={modalTarget.operation}
          defaultReferenceType={modalTarget.referenceType}
          defaultReferenceId={modalTarget.referenceId}
          defaultAmount={modalTarget.amount}
          onDone={() => queryClient.invalidateQueries({ queryKey: ledgerKey })}
        />
      )}
    </PageGuard>
  );
};
